import React, {
    useCallback, useMemo,
} from 'react'
import {
    DatePicker as FluentuiDatePicker, Calendar, defaultCalendarStrings, DayOfWeek,
} from '@fluentui/react'
import 'react-phone-input-2/lib/style.css'
import { createOpenDaysDateRange } from 'helpers/methods/createDateRange'
import Time from 'helpers/methods/time'
import useTranslate from 'helpers/hooks/useTranslate'
import OpenDays from 'requests/objects/openDays'

const calendarStrings = {
    en: {
        months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        shortDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
        goToToday: 'Go to today',
    },
    fr: {
        months: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
        shortMonths: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'],
        days: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
        shortDays: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
        goToToday: "Aller à aujourd'hui",
    },
    nl: {
        months: ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'],
        shortMonths: ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
        days: ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'],
        shortDays: ['Z', 'M', 'D', 'W', 'D', 'V', 'Z'],
        goToToday: 'Ga naar vandaag',
    },
    es: {
        months: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
        shortMonths: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
        days: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
        shortDays: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
        goToToday: 'Ir a hoy',
    },
}

const dateFormats = {
    fr: { order: ['day', 'month', 'year'], separator: '/', placeholder: 'jj/mm/aaaa' },
    en: { order: ['month', 'day', 'year'], separator: '/', placeholder: 'mm/dd/yyyy' },
    es: { order: ['day', 'month', 'year'], separator: '/', placeholder: 'dd/mm/aaaa' },
    nl: { order: ['day', 'month', 'year'], separator: '-', placeholder: 'dd-mm-yyyy' },
}

const normalizeLang = lang => {
    const normalizedLang = lang?.toLowerCase?.().split('-')[0]

    return dateFormats[normalizedLang] ? normalizedLang : 'en'
}

const getDateFormat = lang => dateFormats[normalizeLang(lang)] ?? dateFormats.en

const normalizeDate = value => {
    if (!value)
        return undefined

    if (value instanceof Date)
        return Number.isNaN(value.getTime()) ? undefined : value

    const dateValue = new Date(value)

    return Number.isNaN(dateValue.getTime()) ? undefined : dateValue
}

const getDateKey = value => {
    const dateValue = normalizeDate(value)

    if (!dateValue)
        return ''

    return [
        dateValue.getFullYear(),
        dateValue.getMonth(),
        dateValue.getDate(),
    ].join('-')
}

const formatDateByLang = (date, lang) => {
    const dateValue = normalizeDate(date)

    if (!dateValue)
        return ''

    const values = {
        day: dateValue.getDate().toString().padStart(2, '0'),
        month: (dateValue.getMonth() + 1).toString().padStart(2, '0'),
        year: dateValue.getFullYear().toString(),
    }
    const format = getDateFormat(lang)

    return format.order.map(key => values[key]).join(format.separator)
}

/**
 * @typedef {object} DatePickerProps
 * @property {Date | string | null=} date date
 * @property {(date: Date) => void} onChangeDate onChangeDate
 * @property {string=} label label
 * @property {Date=} minDate minDate
 * @property {Date=} maxDate maxDate
 * @property {string=} placeholder placeholder
 * @property {string=} [lang] lang
 * @property {OpenDays} [openDays] openDays
 * @property {boolean=} [borderless] borderless
 * @property {boolean=} [required] required
 * @property {boolean=} [readOnly] readOnly
 * @property {boolean=} [disabled] disabled
 * @property {string=} [errorMessage] errorMessage
 */

/**
 * Date Picker
 * @param {DatePickerProps} props props
 * @returns {JSX.Element} Element
 */
export default function DatePicker({
    date,
    onChangeDate,
    label,
    minDate = new Date(),
    maxDate: maxDateProp,
    placeholder,
    lang = 'en',
    openDays = null,
    borderless = false,
    required = false,
    readOnly = false,
    disabled = false,
    errorMessage = '',
}) {
    const currentLang = normalizeLang(lang)
    const { tKey } = useTranslate({ lang: currentLang })
    const dateFormat = getDateFormat(currentLang)
    const selectedDateKey = getDateKey(date)
    const minDateKey = getDateKey(minDate)
    const maxDateKey = getDateKey(maxDateProp)
    // Use day-level keys because callers often pass new Date() inline on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selectedDate = useMemo(() => normalizeDate(date), [selectedDateKey])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const minSelectableDate = useMemo(() => normalizeDate(minDate) || new Date(), [minDateKey])
    const datePickerStrings = useMemo(() => ({
        ...defaultCalendarStrings,
        ...calendarStrings[currentLang],
    }), [currentLang])
    const [
        maxDate,
        dateRange,
    ] = useMemo(
        () => {
            const maxDatePropValue = normalizeDate(maxDateProp)
            const max = maxDatePropValue || new Date(minSelectableDate)
            if (!maxDatePropValue)
                max.setMonth(max.getMonth() + 6) // max date is month later than min date

            return [
                max,
                openDays ? createOpenDaysDateRange(openDays, minSelectableDate, max) : [],
            ]
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [maxDateKey, minSelectableDate, openDays],
    )
    const formatDate = useCallback(d => formatDateByLang(d, currentLang), [currentLang])
    const datePlaceholder = placeholder || dateFormat.placeholder || tKey('chooseDate')

    const CalendarAs = useCallback(calendarProps => (
        <Calendar
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...calendarProps}
            isMonthPickerVisible={false}
            autoNavigateOnSelection
            showCloseButton
            showMonthPickerAsOverlay
            showSixWeeksByDefault
            firstDayOfWeek={DayOfWeek.Monday}
            minDate={minSelectableDate}
            maxDate={maxDate}
            restrictedDates={dateRange}
            strings={datePickerStrings}
        />
    ), [datePickerStrings, dateRange, maxDate, minSelectableDate])

    return (
        <FluentuiDatePicker
            key={currentLang}
            placeholder={datePlaceholder}
            value={selectedDate}
            formatDate={formatDate}
            strings={datePickerStrings}
            calendarAs={CalendarAs}
            disabled={disabled}
            isRequired={required}
            styles={{
                root: readOnly || disabled ? { pointerEvents: 'none' } : {},
                icon: readOnly || disabled ? { display: 'none' } : {},
            }}
            onSelectDate={dt => {
                if (!dt) {
                    onChangeDate(undefined)
                } else if (selectedDate && getDateKey(dt) === getDateKey(selectedDate)) {
                    onChangeDate(undefined)
                } else {
                    const newDate = Time(dt).set(7, 'hour')
                    onChangeDate(new Date(newDate))
                }
            }}
            textField={{
                borderless,
                required,
                label,
                disabled,
                readOnly,
                errorMessage,
            }}
        />
    )
}
